import React from 'react';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';

export default function Kvalitet() {
  return (
    <>
      <SEO
        title="Fastlane ApS :: Quality"
        description="Kvalitet kan defineres som forholdet mellem et menneskes oplevelse af og forventninger til en given service eller et givent produkt. Læs om Fastlanes kvalitetssikring."
        canonicalUrl="https://fastlane.dk/kvalitet"
      />

      <PageHeader
        title="Kvalitet"
        subtitle="Forholdet mellem oplevelse og forventning – nøgleordet for succes er kommunikation."
        breadcrumbs={[{ label: 'Kvalitet' }]}
      />

      <div className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-slate max-w-none space-y-6 text-slate-700 leading-relaxed">
            <p className="text-lg font-medium text-[#0f2b48]">
              Kvalitet kan defineres som; forholdet mellem et menneskes oplevelse af og forventninger til en given service eller et givent produkt.
            </p>

            <p>
              Altså er kvalitet yderst subjektivt og helt afhængig af hvem der ser, føler eller oplever.
            </p>

            <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 my-6">
              <h2 className="text-base font-bold text-[#0f2b48] mb-3">
                Transport og relaterede services karakteriseres af, at der altid er mange involverede parter, såsom:
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
                <span className="flex items-center gap-2"><i className="fa-solid fa-check text-sky-600 text-xs" /> køber</span>
                <span className="flex items-center gap-2"><i className="fa-solid fa-check text-sky-600 text-xs" /> sælger</span>
                <span className="flex items-center gap-2"><i className="fa-solid fa-check text-sky-600 text-xs" /> afsender</span>
                <span className="flex items-center gap-2"><i className="fa-solid fa-check text-sky-600 text-xs" /> modtager</span>
                <span className="flex items-center gap-2"><i className="fa-solid fa-check text-sky-600 text-xs" /> lagermedarbejdere</span>
                <span className="flex items-center gap-2"><i className="fa-solid fa-check text-sky-600 text-xs" /> vognmand</span>
                <span className="flex items-center gap-2"><i className="fa-solid fa-check text-sky-600 text-xs" /> chauffør</span>
                <span className="flex items-center gap-2"><i className="fa-solid fa-check text-sky-600 text-xs" /> agent</span>
                <span className="flex items-center gap-2"><i className="fa-solid fa-check text-sky-600 text-xs" /> speditør</span>
                <span className="flex items-center gap-2"><i className="fa-solid fa-check text-sky-600 text-xs" /> og mange andre</span>
              </div>
            </div>

            <p>
              Den rigtige kvalitet er når de kritiske involverede parter for en given transport har en god oplevelse gang efter gang. Det forudsætter, at alle kritiske involverede ved, hvad de kan forvente sig og hvad de får.
            </p>

            <p>
              Nøgleordet for succes er kommunikation. Med udgangspunkt i omfattende erfaring og viden varetager vi afklaring, timing, udførsel, opfølgning og information på vegne af vore kunder.
            </p>

            <h3 className="text-xl font-bold text-[#0f2b48] pt-4">Fastlane ApS</h3>
            <p>
              Vi arbejder løbende med at forbedre kvaliteten. Målsætningen er bedre kundetilfredshed.
            </p>
            <p className="font-semibold text-slate-800">Vi har fokus på følgende:</p>
            <ul className="list-disc pl-5 space-y-1.5 text-sm">
              <li>Processer, d.v.s. de rigtige i den rigtige rækkefølge</li>
              <li>Præcis afklaring/kommunikation</li>
              <li>Proaktiv opfølgning</li>
              <li>Aktiv akkumulering af viden</li>
              <li>Interne instruktioner og eksterne instruktioner</li>
            </ul>

            <h3 className="text-xl font-bold text-[#0f2b48] pt-4">Leverandører</h3>
            <p>
              Alle vore leverandører er omhyggeligt udvalgt, og vi følger løbende op på deres præstationer. Målsætningen er at vide præcist hvem der er bedst hvor, så vi hurtigt kan vælge den rigtige til enhver opgave.
            </p>

            <h3 className="text-xl font-bold text-[#0f2b48] pt-4">Afsender/Kunder</h3>
            <p>
              Vores erfaring har vist at mange kvalitetsmangler opstår allerede hos afsender eller hos vores kunder. Typisk kan disse mangler forebygges ved simple tiltag såsom:
            </p>
            <ul className="list-disc pl-5 space-y-1.5 text-sm">
              <li>Korrekt mærkning af godset</li>
              <li>Korrekt emballering og pakning af godset</li>
              <li>Korrekte adresser på afsender/modtager samt kontaktperson + telefonnr.</li>
            </ul>

            <div className="mt-10 p-6 sm:p-8 bg-sky-50/60 rounded-2xl border border-sky-100">
              <h3 className="text-xl font-bold text-[#0f2b48] mb-2 flex items-center gap-2">
                <i className="fa-solid fa-circle-question text-sky-600" aria-hidden="true" />
                <span>Hjælp til selvhjælp</span>
              </h3>
              <p className="text-sm text-slate-600 mb-6">
                Herunder instruktioner i PDF-format til at hjælpe dig med af forebygge fejl. Klik på linkene og læs instruktionen på skærmen eller vælg funktionen - "save as" når PDF-filen er åben og gem dokumentet på din computer.
              </p>

              <div className="space-y-3">
                <a
                  href="https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/instruktion-godsets-vej.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-3.5 bg-white rounded-xl border border-slate-200 hover:border-sky-400 hover:shadow-xs transition-all group"
                >
                  <div className="flex items-center gap-3">
                    <i className="fa-solid fa-file-pdf text-red-600 text-xl" aria-hidden="true" />
                    <span className="font-semibold text-slate-800 group-hover:text-sky-800 text-sm">Godsets vej</span>
                  </div>
                  <span className="text-xs font-medium text-sky-600 group-hover:translate-x-1 transition-transform">
                    Åbn PDF <i className="fa-solid fa-arrow-up-right-from-square ml-1 text-[10px]" />
                  </span>
                </a>

                <a
                  href="https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/instruktion-maerking.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-3.5 bg-white rounded-xl border border-slate-200 hover:border-sky-400 hover:shadow-xs transition-all group"
                >
                  <div className="flex items-center gap-3">
                    <i className="fa-solid fa-file-pdf text-red-600 text-xl" aria-hidden="true" />
                    <span className="font-semibold text-slate-800 group-hover:text-sky-800 text-sm">Mærkning af Godset</span>
                  </div>
                  <span className="text-xs font-medium text-sky-600 group-hover:translate-x-1 transition-transform">
                    Åbn PDF <i className="fa-solid fa-arrow-up-right-from-square ml-1 text-[10px]" />
                  </span>
                </a>

                <a
                  href="https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/instruktion-emballering.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-3.5 bg-white rounded-xl border border-slate-200 hover:border-sky-400 hover:shadow-xs transition-all group"
                >
                  <div className="flex items-center gap-3">
                    <i className="fa-solid fa-file-pdf text-red-600 text-xl" aria-hidden="true" />
                    <span className="font-semibold text-slate-800 group-hover:text-sky-800 text-sm">Emballering og Pakning af Godset</span>
                  </div>
                  <span className="text-xs font-medium text-sky-600 group-hover:translate-x-1 transition-transform">
                    Åbn PDF <i className="fa-solid fa-arrow-up-right-from-square ml-1 text-[10px]" />
                  </span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
